import { useEffect, useState, type FormEvent } from 'react';
import { Smartphone } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import userService from '../../services/user';
import { errorMessage } from '../../utils/apiResponse';

/**
 * Account & Security → Mobile. A person's own mobile, which HR can also set
 * on their staff profile. Where the organisation has the Sales module, a
 * call from it is put against them by name.
 */
export default function MobileNumberCard() {
  const [value, setValue] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    userService.getMyMobile()
      .then((r) => { if (r.success) setValue(r.data.mobile_phone ?? ''); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const r = await userService.setMyMobile(value.trim());
      if (!r.success) throw r;
      setValue(r.data.mobile_phone ?? '');
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err, 'Could not save your mobile'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="h-5 w-5" aria-hidden />Mobile
        </CardTitle>
        <CardDescription>
          So a call from your mobile is put against you by name. Your employer's HR can see and change it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="flex flex-col sm:flex-row sm:items-end gap-2 min-w-0">
          <div className="min-w-0">
            <label htmlFor="my-mobile" className="block text-xs font-medium text-neutral-600 mb-1">Mobile number</label>
            <input
              id="my-mobile"
              type="tel"
              autoComplete="tel"
              value={value}
              disabled={!loaded}
              onChange={(e) => { setValue(e.target.value); setSaved(false); }}
              placeholder="0412 345 678"
              className="w-full sm:w-64 rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0"
            />
          </div>
          <Button type="submit" size="sm" isLoading={saving} disabled={!loaded}>Save</Button>
        </form>
        {saved && <p className="text-sm text-emerald-700 mt-2">{value ? 'Saved.' : 'Removed.'}</p>}
        {error && <p className="text-sm text-red-700 mt-2 break-words">{error}</p>}
      </CardContent>
    </Card>
  );
}
